import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogOut, Package, CheckCircle2, Clock, DollarSign, Award, Wallet, Send, TrendingUp, Copy, Search } from "lucide-react";
import api, { formatApiError } from "@/services/api";
import { useAgentSession } from "@/context/AgentContext";
import { useLang } from "@/context/LanguageContext";
import { fmtMoney, fmtDate } from "@/utils/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Logo from "@/components/Logo";
import { transferSkipsId } from "@/utils/knownClient";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import StatCard from "@/components/StatCard";
import StatusBadge from "@/components/StatusBadge";
import { useSingleFlightMutation } from "@/hooks/useSingleFlightMutation";

export default function AgentDashboard() {
  const { agent, logout } = useAgentSession();
  const { t } = useLang();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirmModal, setConfirmModal] = useState(null); // transfer object
  const [clientDocument, setClientDocument] = useState(""); // CIN oswa NIF (yon sèl chan)
  const [idPhoto, setIdPhoto] = useState(null); // { name, contentType, base64 }
  const [photoPreview, setPhotoPreview] = useState("");
  const [wModal, setWModal] = useState(false);
  const [wForm, setWForm] = useState({ amount: "", method: "NatCash", natcash_number: "", natcash_name: "" });
  const [transferTab, setTransferTab] = useState("pending"); // pending | done
  const [transferSearch, setTransferSearch] = useState("");

  useEffect(() => { if (!agent) navigate("/agent", { replace: true }); }, [agent, navigate]);

  const phone = agent?.phone;

  const { data: summary, isLoading: sL } = useQuery({
    queryKey: ["agent-summary", phone],
    queryFn: async () => (await api.get(`/agent/${encodeURIComponent(phone)}/summary`)).data,
    enabled: !!phone,
  });
  const { data: transfers = [], isLoading: tL } = useQuery({
    queryKey: ["agent-transfers", phone],
    queryFn: async () => (await api.get(`/agent/${encodeURIComponent(phone)}/transfers`)).data,
    enabled: !!phone,
  });
  const { data: withdrawals = [] } = useQuery({
    queryKey: ["agent-withdrawals", phone],
    queryFn: async () => (await api.get(`/agent/${encodeURIComponent(phone)}/withdrawals`)).data,
    enabled: !!phone,
  });
  const { data: zelle } = useQuery({
    queryKey: ["zelle"],
    queryFn: async () => (await api.get("/zelle")).data,
  });

  const pendingTransfers = (transfers || []).filter((x) => x.status === "pending" || x.status === "processing");
  const doneTransfers = (transfers || []).filter((x) => x.status === "delivered" || x.status === "canceled");

  const normSearch = (transferSearch || "").trim().toLowerCase().replace(/^#/, "");
  const filterTransfers = (list) => {
    if (!normSearch) return list;
    const q = normSearch.replace(/\s+/g, "");
    return list.filter((x) => {
      const tid = String(x.transfer_id || "").toLowerCase();
      const name = String(x.client_name || "").toLowerCase();
      const phone = String(x.client_phone || x.client_whatsapp || "").toLowerCase();
      return tid.includes(q) || tid.replace(/\s+/g, "").includes(q)
        || name.includes(normSearch) || phone.includes(q);
    });
  };
  const visibleTransfers = filterTransfers(
    transferTab === "pending" ? pendingTransfers : doneTransfers,
  );

  const splitDocument = (raw) => {
    const doc = String(raw || "").trim();
    if (!doc) return { client_cin: "", client_nif: "" };
    // Menm règ ak bot Telegram: tout chif = NIF, sinon CIN
    if (/^\d+$/.test(doc)) return { client_cin: "", client_nif: doc };
    return { client_cin: doc, client_nif: "" };
  };

  const onPickPhoto = (file) => {
    if (!file) { setIdPhoto(null); setPhotoPreview(""); return; }
    if (!file.type.startsWith("image/")) {
      toast.error("Chwazi yon imaj (JPG/PNG)");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Foto twò gwo (max 8MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      setPhotoPreview(dataUrl);
      setIdPhoto({ name: file.name, contentType: file.type, base64: dataUrl });
    };
    reader.readAsDataURL(file);
  };

  const openConfirm = (t2) => {
    setConfirmModal(t2);
    setClientDocument("");
    setIdPhoto(null);
    setPhotoPreview("");
  };

  const confirmMut = useSingleFlightMutation({
    mutationFn: async () => {
      const docs = splitDocument(clientDocument);
      return (await api.post(`/agent/${encodeURIComponent(phone)}/confirm/${confirmModal.transfer_id}`, {
        ...docs,
        agent_phone: phone,
        photo_base64: idPhoto?.base64 || null,
        photo_content_type: idPhoto?.contentType || "image/jpeg",
      })).data;
    },
    onSuccess: () => {
      toast.success("Livrezon konfime!");
      qc.invalidateQueries({ queryKey: ["agent-summary", phone] });
      qc.invalidateQueries({ queryKey: ["agent-transfers", phone] });
      setConfirmModal(null);
      setClientDocument("");
      setIdPhoto(null);
      setPhotoPreview("");
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const wMut = useSingleFlightMutation({
    mutationFn: async () => (await api.post("/withdrawals", {
      agent_phone: phone,
      amount: parseFloat(wForm.amount) || 0,
      method: wForm.method,
      natcash_number: wForm.natcash_number,
      natcash_name: wForm.natcash_name,
    })).data,
    onSuccess: () => {
      toast.success("Demann retrè voye");
      qc.invalidateQueries({ queryKey: ["agent-withdrawals", phone] });
      setWModal(false); setWForm({ amount: "", method: "NatCash", natcash_number: "", natcash_name: "" });
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const handleLogout = () => { logout(); navigate("/agent"); };

  if (!agent) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-2">
          <Logo size={36} className="min-w-0 [&_.logo-text]:hidden sm:[&_.logo-text]:block" />
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <div className="hidden sm:block text-right mr-2">
              <div className="text-sm font-semibold" data-testid="agent-name">{agent.name}</div>
              <div className="text-xs text-muted-foreground">{agent.phone}</div>
            </div>
            <LanguageSwitcher />
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="agent-logout-btn" className="gap-2 px-2 sm:px-3">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">{t("logout")}</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-6 space-y-5">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <h1 data-testid="agent-welcome-title" className="text-xl sm:text-3xl font-heading font-bold break-words">
            {t("welcome")}, <span data-testid="agent-name-mobile">{agent.name}</span>
          </h1>
          <p data-testid="agent-area-sub" className="text-xs text-muted-foreground mt-0.5">{t("agent_area_sub")}</p>
        </motion.div>

        {/* Zelle info card — copyable fields */}
        {zelle && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass rounded-xl p-4" data-testid="agent-zelle-card">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-brand-gold font-semibold"><Send className="h-3 w-3" /> {t("zelle_info")}</div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs"
                data-testid="zelle-copy-all-btn"
                onClick={async () => {
                  const block = [
                    zelle.name && `Non: ${zelle.name}`,
                    (zelle.email || zelle.phone) && `Nimewo/Email Zelle: ${zelle.email || zelle.phone}`,
                    zelle.whatsapp && `WhatsApp: ${zelle.whatsapp}`,
                    zelle.instructions && `Enstriksyon: ${zelle.instructions}`,
                  ].filter(Boolean).join("\n");
                  try {
                    await navigator.clipboard.writeText(block);
                    toast.success("Kont Zelle kopye");
                  } catch {
                    toast.error("Pa ka kopye");
                  }
                }}
              >
                <Copy className="h-3.5 w-3.5" /> Kopi tout
              </Button>
            </div>
            <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { label: t("zelle_account_name"), value: zelle.name, id: "name" },
                { label: t("zelle_handle"), value: zelle.email || zelle.phone, id: "handle" },
                { label: t("zelle_whatsapp"), value: zelle.whatsapp, id: "whatsapp" },
              ].filter((f) => f.value).map((f) => (
                <div key={f.id} className="min-w-0 rounded-lg border border-border/60 bg-background/40 p-2.5">
                  <div className="flex items-center justify-between gap-1">
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{f.label}</div>
                    <button
                      type="button"
                      className="shrink-0 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary"
                      title="Kopi"
                      data-testid={`zelle-copy-${f.id}`}
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(String(f.value || ""));
                          toast.success("Kopye");
                        } catch {
                          toast.error("Pa ka kopye");
                        }
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="font-semibold font-mono text-xs break-all select-all mt-0.5">{f.value || "—"}</div>
                </div>
              ))}
            </div>
            {zelle.instructions && (
              <div className="mt-2 flex items-start gap-2 rounded-lg border border-border/60 bg-background/40 p-2.5">
                <div className="min-w-0 flex-1 text-xs text-muted-foreground break-words select-all">{zelle.instructions}</div>
                <button
                  type="button"
                  className="shrink-0 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary"
                  data-testid="zelle-copy-instructions"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(String(zelle.instructions || ""));
                      toast.success("Kopye");
                    } catch {
                      toast.error("Pa ka kopye");
                    }
                  }}
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* Stats — always even 2-col on mobile so no lonely card */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
          {sL ? [...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />) : (
            <>
              <StatCard compact label={t("total_transfers")} value={summary?.total_transfers ?? 0} icon={Package} accent="blue" delay={0} testId="agent-stat-total" />
              <StatCard compact label={t("delivered")} value={summary?.delivered ?? 0} icon={CheckCircle2} accent="green" delay={0.05} testId="agent-stat-delivered" />
              <StatCard compact label={t("pending")} value={summary?.pending ?? 0} icon={Clock} accent="gold" delay={0.1} testId="agent-stat-pending" />
              <StatCard compact label={t("total_amount")} value={fmtMoney(summary?.total_amount ?? 0)} icon={DollarSign} accent="blue" delay={0.15} testId="agent-stat-amount" />
            </>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-3">
          <StatCard compact label={t("profit_total")} value={fmtMoney(summary?.total_profit ?? 0)} icon={TrendingUp} accent="green" testId="agent-stat-profit-total" />
          <StatCard compact label={t("profit_withdrawn")} value={fmtMoney(summary?.withdrawn ?? 0)} icon={Wallet} accent="slate" testId="agent-stat-withdrawn" />
          <StatCard compact label={t("profit_remaining")} value={fmtMoney(summary?.remaining_profit ?? 0)} icon={Award} accent={(summary?.remaining_profit ?? 0) < 0 ? "red" : "gold"} testId="agent-stat-remaining" />
          <StatCard compact label={t("total_funded")} value={fmtMoney(summary?.total_funded ?? 0)} icon={DollarSign} accent="gold" testId="agent-stat-funded" />
          <StatCard compact label={t("cash_delivered")} value={fmtMoney(summary?.cash_delivered ?? 0)} icon={Send} accent="blue" testId="agent-stat-cash-delivered" />
          <StatCard compact label={t("cash_in_hand")} value={fmtMoney(summary?.cash_in_hand ?? 0)} icon={Wallet} accent="green" testId="agent-stat-cash-in-hand" />
        </div>

        <div className="flex justify-stretch sm:justify-end">
          <Button className="w-full sm:w-auto bg-brand-gold hover:bg-brand-goldHover text-slate-900 font-semibold gap-2" onClick={() => setWModal(true)} data-testid="request-withdrawal-btn">
            <Wallet className="h-4 w-4" /> {t("request_withdrawal")}
          </Button>
        </div>

        <Tabs defaultValue="transfers">
          <TabsList data-testid="agent-tabs">
            <TabsTrigger value="transfers" data-testid="tab-transfers">{t("transfers")}</TabsTrigger>
            <TabsTrigger value="withdrawals" data-testid="tab-withdrawals">{t("withdrawals")}</TabsTrigger>
          </TabsList>
          <TabsContent value="transfers" className="mt-4 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                value={transferSearch}
                onChange={(e) => setTransferSearch(e.target.value)}
                placeholder={t("search_transfer_ph")}
                className="pl-9 h-10"
                data-testid="agent-transfer-search"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant={transferTab === "pending" ? "default" : "outline"}
                className={transferTab === "pending" ? "bg-brand-blue text-white" : ""}
                onClick={() => setTransferTab("pending")}
                data-testid="agent-tab-pending"
              >
                An atant ({pendingTransfers.length})
              </Button>
              <Button
                size="sm"
                variant={transferTab === "done" ? "default" : "outline"}
                className={transferTab === "done" ? "bg-brand-blue text-white" : ""}
                onClick={() => setTransferTab("done")}
                data-testid="agent-tab-done"
              >
                Konplete ({doneTransfers.length})
              </Button>
            </div>
            <div className="rounded-2xl border border-border bg-card overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>No# Transfè</TableHead>
                    <TableHead>Ki voye</TableHead>
                    <TableHead>{t("client_name")}</TableHead>
                    <TableHead className="hidden sm:table-cell">{t("phone")}</TableHead>
                    <TableHead>Pou bay</TableHead>
                    <TableHead className="hidden md:table-cell">Komisyon</TableHead>
                    <TableHead className="hidden md:table-cell">Dat</TableHead>
                    <TableHead>Estati</TableHead>
                    <TableHead className="text-right hidden sm:table-cell">Aksyon</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tL && [...Array(3)].map((_, i) => <TableRow key={i}><TableCell colSpan={9}><Skeleton className="h-8" /></TableCell></TableRow>)}
                  {!tL && visibleTransfers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-10 text-muted-foreground">
                        {normSearch ? t("no_transfer_match") : "Pa gen transfè"}
                      </TableCell>
                    </TableRow>
                  )}
                  {visibleTransfers.map((t2) => {
                    const canTreat = t2.status !== "delivered" && t2.status !== "canceled";
                    return (
                      <TableRow
                        key={t2.transfer_id}
                        data-testid={`agent-transfer-${t2.transfer_id}`}
                        className={canTreat ? "cursor-pointer hover:bg-secondary/60 active:bg-secondary/80" : undefined}
                        onClick={() => { if (canTreat) openConfirm(t2); }}
                      >
                        <TableCell className="font-mono text-xs text-brand-gold">{t2.transfer_id}</TableCell>
                        <TableCell className="text-sm">{t2.sender_name || "—"}</TableCell>
                        <TableCell className="font-medium">{t2.client_name}</TableCell>
                        <TableCell className="text-sm hidden sm:table-cell">{t2.client_phone}</TableCell>
                        <TableCell className="font-semibold">{fmtMoney(t2.amount)}</TableCell>
                        <TableCell className="text-sm text-emerald-600 hidden md:table-cell">
                          {t2.status === "delivered" ? fmtMoney(t2.agent_profit ?? 0) : "—"}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground hidden md:table-cell">{fmtDate(t2.created_at)}</TableCell>
                        <TableCell><StatusBadge status={t2.status} /></TableCell>
                        <TableCell className="text-right hidden sm:table-cell">
                          {canTreat && (
                            <Button
                              size="sm"
                              className="bg-brand-blue hover:bg-brand-blueHover text-white"
                              onClick={(e) => { e.stopPropagation(); openConfirm(t2); }}
                              data-testid={`process-${t2.transfer_id}`}
                            >
                              {t("process_delivery")}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {transferTab === "pending" && !tL && pendingTransfers.length > 0 && (
              <p className="text-xs text-muted-foreground sm:hidden">
                {t("tap_row_to_treat")}
              </p>
            )}
          </TabsContent>
          <TabsContent value="withdrawals" className="mt-4">
            <div className="rounded-2xl border border-border bg-card overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("amount")}</TableHead>
                    <TableHead>Metòd</TableHead>
                    <TableHead>Dat</TableHead>
                    <TableHead>Estati</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {withdrawals.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground">Pa gen retrè</TableCell></TableRow>}
                  {withdrawals.map((w) => (
                    <TableRow key={w.id}>
                      <TableCell className="font-semibold">{fmtMoney(w.amount)}</TableCell>
                      <TableCell>
                        {w.method}
                        {w.natcash_number && <span className="text-xs text-muted-foreground"> — {w.natcash_number}</span>}
                        {w.natcash_name && <div className="text-xs text-muted-foreground">{w.natcash_name}</div>}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{fmtDate(w.created_at)}</TableCell>
                      <TableCell><StatusBadge status={w.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Confirm delivery modal */}
      <Dialog open={!!confirmModal} onOpenChange={(o) => !o && setConfirmModal(null)}>
        <DialogContent className="bg-card max-w-md w-[calc(100%-1.5rem)] sm:w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("confirm_delivery")}</DialogTitle></DialogHeader>
          {confirmModal && (
            <div className="space-y-4">
              {transferSkipsId(confirmModal) && (
                <p className="text-sm text-emerald-700" data-testid="confirm-known-client">
                  Moun sa a nou konnen. No# {confirmModal.transfer_id} apwouve san pyès idantite.
                </p>
              )}
              <div className="rounded-xl bg-secondary/50 p-4 text-sm space-y-1">
                <div className="font-mono text-brand-gold">{confirmModal.transfer_id}</div>
                {confirmModal.sender_name && (
                  <div className="text-xs text-muted-foreground">Ki voye: {confirmModal.sender_name}</div>
                )}
                <div className="font-semibold">Resevwa: {confirmModal.client_name}</div>
                <div>Pou bay: <span className="font-semibold">{fmtMoney(confirmModal.amount)}</span></div>
                <div className="text-emerald-600">Komisyon ou: {fmtMoney(confirmModal.agent_profit ?? 0)}</div>
                {(confirmModal.client_whatsapp || confirmModal.client_phone) && (
                  <div className="text-xs text-muted-foreground">WhatsApp: {confirmModal.client_whatsapp || confirmModal.client_phone}</div>
                )}
                {confirmModal.client_address && (
                  <div className="text-xs text-muted-foreground">Adrès: {confirmModal.client_address}</div>
                )}
              </div>
              {!transferSkipsId(confirmModal) && <>
              <div>
                <Label>{t("cin_or_nif")}</Label>
                <Input
                  value={clientDocument}
                  onChange={(e) => setClientDocument(e.target.value)}
                  placeholder={t("cin_or_nif")}
                  data-testid="confirm-document"
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground mt-1">{t("cin_or_nif_hint")}</p>
              </div>
              <div>
                <Label>Foto pyès idantite</Label>
                <Input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="mt-1.5 cursor-pointer"
                  data-testid="confirm-id-photo"
                  onChange={(e) => onPickPhoto(e.target.files?.[0])}
                />
                <p className="text-xs text-muted-foreground mt-1">{t("id_photo_required")}</p>
                {photoPreview && (
                  <div className="mt-3 rounded-xl overflow-hidden border border-border bg-slate-950">
                    <img src={photoPreview} alt="Preview ID" className="max-h-48 w-full object-contain" data-testid="confirm-id-preview" />
                  </div>
                )}
              </div>
              </>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmModal(null)}>{t("cancel")}</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => confirmMut.mutate()}
              disabled={confirmMut.isPending || (!transferSkipsId(confirmModal) && (clientDocument.trim().length < 3 || !idPhoto))}
              data-testid="confirm-delivery-btn"
            >
              {confirmMut.isPending ? t("loading") : t("confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdrawal modal */}
      <Dialog open={wModal} onOpenChange={setWModal}>
        <DialogContent className="bg-card">
          <DialogHeader><DialogTitle>{t("request_withdrawal")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl bg-brand-gold/10 border border-brand-gold/20 p-4 text-sm">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Pwofi disponib</div>
              <div className="text-2xl font-heading font-bold text-amber-500 mt-1">{fmtMoney(summary?.remaining_profit ?? 0)}</div>
            </div>
            <div><Label>{t("amount")} (USD)</Label><Input type="number" step="0.01" value={wForm.amount} onChange={(e) => setWForm({ ...wForm, amount: e.target.value })} data-testid="withdraw-amount" /></div>
            <div>
              <Label>Metòd</Label>
              <Select value={wForm.method} onValueChange={(v) => setWForm({ ...wForm, method: v })}>
                <SelectTrigger className="bg-background" data-testid="withdraw-method"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover"><SelectItem value="NatCash">NatCash (HTG)</SelectItem><SelectItem value="Cash">Cash</SelectItem></SelectContent>
              </Select>
            </div>
            {wForm.method === "NatCash" && (
              <>
                <div><Label>{t("natcash_number")}</Label><Input value={wForm.natcash_number} onChange={(e) => setWForm({ ...wForm, natcash_number: e.target.value })} placeholder="+509 ..." data-testid="withdraw-natcash" /></div>
                <div><Label>{t("natcash_name")}</Label><Input value={wForm.natcash_name} onChange={(e) => setWForm({ ...wForm, natcash_name: e.target.value })} placeholder="Non sou NatCash" data-testid="withdraw-natcash-name" /></div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWModal(false)}>{t("cancel")}</Button>
            <Button className="bg-brand-blue hover:bg-brand-blueHover text-white" onClick={() => wMut.mutate()} disabled={wMut.isPending || !wForm.amount} data-testid="withdraw-submit">
              {wMut.isPending ? t("loading") : t("confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
